import { Theme, useMediaQuery } from "@mui/material";
import { ProductType, ServiceType } from "@origin-digital/event-dispatcher";
import { Box, Section, Tab, Tabs } from "@origin-digital/ods-core";
import {
  IconElectricity,
  IconGas,
  IconHotWater,
  IconInternet,
  IconLPG,
} from "@origin-digital/ods-icons";
import {
  getAccountServiceIdentifier,
  useUserContext,
} from "@origin-digital/user-context-provider";
import React from "react";
import styled from "styled-components";

interface ServiceDisplayProps {
  show: (productType?: ProductType) => boolean;
  icon?: (isSmallScreen: boolean) => React.ReactNode;
  label?: string;
}

const SERVICE_DISPLAY_PROPS: Record<ServiceType, ServiceDisplayProps> = {
  [ServiceType.ELECTRICITY]: {
    show: (productType) =>
      productType !== ProductType.ESAAS &&
      productType !== ProductType.COMMUNITY_BATTERY,
    icon: (isSmallScreen: boolean) => (
      <IconElectricity
        color="neutral"
        size={isSmallScreen ? "xsmall" : "small"}
      />
    ),
    label: "Electricity",
  },
  [ServiceType.NATURAL_GAS]: {
    show: (productType) => productType !== ProductType.ESAAS,
    icon: (isSmallScreen: boolean) => (
      <IconGas color="neutral" size={isSmallScreen ? "xsmall" : "small"} />
    ),
    label: "Gas",
  },
  [ServiceType.HOT_WATER]: {
    show: () => true,
    icon: (isSmallScreen: boolean) => (
      <IconHotWater color="neutral" size={isSmallScreen ? "xsmall" : "small"} />
    ),
    label: "Hot water",
  },
  [ServiceType.LPG]: {
    show: () => true,
    icon: (isSmallScreen: boolean) => (
      <IconLPG color="neutral" size={isSmallScreen ? "xsmall" : "small"} />
    ),
    label: "LPG",
  },
  [ServiceType.BROADBAND]: {
    show: () => true,
    icon: (isSmallScreen: boolean) => (
      <IconInternet color="neutral" size={isSmallScreen ? "xsmall" : "small"} />
    ),
    label: "Internet",
  },
  [ServiceType.OHM]: {
    show: () => false,
  },
  [ServiceType.VPP_BATTERY]: {
    show: () => false,
  },
  [ServiceType.UNKNOWN]: {
    show: () => false,
  },
};

const StyledSection = styled(Section)`
  padding: 0;

  /* stylelint-disable-next-line media-query-no-invalid */
  @media (min-width: ${({ theme }) => theme.breakpoints.values.sm}px) {
    padding: 0 ${({ theme }) => theme.space.medium}px;
  }
`;

export const ServiceSelector = () => {
  const { property, setService } = useUserContext();

  const isSmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const accountServicesDisplayList =
    property?.accountServices.filter((accountService) =>
      SERVICE_DISPLAY_PROPS[accountService.serviceType].show(
        accountService.productType
      )
    ) ?? [];

  if (!property || accountServicesDisplayList.length <= 1) {
    return null;
  }

  return (
    <StyledSection paddingY="none" backgroundColor="transparent">
      <Box
        data-id="service-selector"
        display="flex"
        className="accelerated-element"
      >
        <Tabs
          className="accelerated-element"
          size="small"
          hideDivider={true}
          muiProps={{
            style: {
              width: isSmallScreen ? "100%" : "auto",
            },
          }}
          variant={
            isSmallScreen && accountServicesDisplayList.length <= 3
              ? "fullWidth"
              : "scrollable"
          }
          scrollButtons={false}
          color="primary"
          iconPosition={isSmallScreen ? "top" : "leading"}
          textSize={isSmallScreen ? "body-small" : "body"}
          value={getAccountServiceIdentifier(property.selectedAccountService)}
          onChange={(_, identifier) => {
            const matchingService = property.accountServices.find(
              (accountService) =>
                getAccountServiceIdentifier(accountService) === identifier
            );
            setService(matchingService!);
          }}
        >
          {accountServicesDisplayList.map((accountService) => (
            <Tab
              className="accelerated-element"
              key={getAccountServiceIdentifier(accountService)}
              value={getAccountServiceIdentifier(accountService)}
              label={SERVICE_DISPLAY_PROPS[accountService.serviceType].label}
              icon={SERVICE_DISPLAY_PROPS[accountService.serviceType].icon!(
                isSmallScreen
              )}
            />
          ))}
        </Tabs>
      </Box>
    </StyledSection>
  );
};
